<template>
  <div
    class="md:container mx-auto flex flex-col items-center font-Almarai h-full relative overflow-x-hidden"
  >
    <div
      @click.prevent="$router.go(-1)"
      class="absolute left-4 top-6 md:hidden text-white cursor-pointer z-50 flex justify-center items-center space-x-2"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="white"
        class="h-5 w-5 cursor-pointer"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
        />
      </svg>
      <small>رجوع</small>
    </div>
    <!-- Logo -->
    <LogoSvg class="absolute" />

    <div class="flex justify-center items-center w-3/12 h-32 flex-shrink-0">
      <img src="@/assets/logo-1.webp" alt="logo" class="w-28" />
    </div>
    <!-- Main Content with Flex Layout -->
    <div
      class="flex flex-col w-full max-w-xl rounded-md md:mx-auto mx-2 flex-1 relative md:top-0 -top-12"
    >
      <!-- Header Section -->
      <div class="flex flex-col items-center gap-y-2 p-6 pb-4 flex-shrink-0">
        <h1 class="text-gray-600 text-md font-Almarai">خطوة 1 من 2</h1>
        <h1 class="md:text-2xl font-bold text-white font-Almarai">
          اختر الباقة المناسبة
        </h1>

        <!-- Country Dropdown -->
        <form class="w-full mx-auto" dir="">
          <div class="relative">
            <select
              dir="rtl"
              id="countries"
              v-model="selectedCountry"
              @change="selectedPaymentMethod = ''"
              class="custom-select bg-[#18181b] font-Almarai border border-[#9d2890] text-gray-200 md:text-md text-sm rounded-lg focus:ring-[#9d2890] focus:border-[#9d2890] block w-full p-2.5"
            >
              <option
                v-for="(methods, country) in paymentData"
                :key="country"
                :value="country"
                class="font-Almarai"
              >
                {{ country }}
              </option>
            </select>
          </div>
        </form>
      </div>

      <!-- Scrollable Content Area -->
      <div class="flex-1 overflow-y-auto px-6 min-h-0 max-h-auto">
        <!-- Country Plans List -->
        <CountryPlansList
          v-if="hasPlansForCountry"
          :plans="currentCountryPlans"
          :selected-index="selectedPlanIndex"
          :currency="currentCountryCurrency"
          @select-plan="selectPlan"
        />
      </div>
      <div class="pb-12"></div>
      <!-- Add padding bottom to prevent button overlay -->
    </div>

    <!-- Fixed Buttons at Bottom of Screen -->
    <div class="fixed md:bottom-12 bottom-0 left-0 right-0 p-4 z-10 bg-[#0B0C0E]">
      <div class="max-w-xl mx-auto flex flex-col gap-y-2">
        <button
          @click.prevent="redirectUserToProvider"
          :disabled="!selectedCountry || (!selectedPaymentMethod && !hasPlansForCountry)"
          :class="{
            'bg-[#9d2890] text-gray-100':
              selectedCountry && (selectedPaymentMethod || hasPlansForCountry),
            'bg-gray-600 text-gray-400 cursor-not-allowed':
              !selectedCountry || (!selectedPaymentMethod && !hasPlansForCountry),
          }"
          class="inline-flex justify-center items-center gap-2 w-full rounded-md py-3 md:w-11/12 md:ml-6 transition-colors"
        >
          <span> متابعة </span>
        </button>
        <button
          @click.prevent="$router.go(-1)"
          class="hidden md:inline-flex justify-center items-center gap-2 w-full border border-basic text-gray-100 rounded-md py-3 md:w-11/12 md:ml-6"
        >
          <span> رجوع </span>
        </button>
        <small
          @click="$router.push('/auth/coupon')"
          class="text-[#9d2890] text-center mt-2 cursor-pointer"
          >لديك كوبون خصم؟</small
        >
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import animationData from "@/assets/loader.json";
import LogoSvg from "@/components/svg/LogoSvg.vue";
import CountryPlansList from "@/components/plans/CountryPlansList.vue";
import { paymentGateways, COUNTRY_PLANS, COUNTRY_CURRENCIES } from "@/constants";
import { mapActions } from "pinia";
import { useAuthStore } from "@/stores/auth";

export default {
  components: {
    LogoSvg,
    CountryPlansList,
  },
  data() {
    return {
      toast: useToast(),
      loaded: false,
      lottieOptions: {
        animationData,
        loop: true,
        autoplay: true,
      },
      jsonFile: animationData,
      selectedCountry: "السودان",
      selectedPaymentMethod: "",
      selectedPlanIndex: 0,
      paymentData: paymentGateways,
      conutries: {
        SD: "السودان",
        EG: "مصر",
        SA: "السعودية",
        AE: "الإمارات",
      },
    };
  },

  computed: {
    availablePaymentMethods() {
      return this.selectedCountry ? this.paymentData[this.selectedCountry] : [];
    },
    currentCountryPlans() {
      return COUNTRY_PLANS[this.selectedCountry] || [];
    },
    currentCountryCurrency() {
      return COUNTRY_CURRENCIES[this.selectedCountry] || "جنيه";
    },
    hasPlansForCountry() {
      return this.currentCountryPlans.length > 0;
    },
    selectedPlan() {
      return this.hasPlansForCountry
        ? this.currentCountryPlans[this.selectedPlanIndex]
        : null;
    },
  },

  methods: {
    ...mapActions(useAuthStore, ["getTokenAccess"]),
    selectPlan(index) {
      this.selectedPlanIndex = index;
    },

    redirectUserToProvider() {
      // Handle countries with plans
      if (this.hasPlansForCountry && this.selectedPlan) {
        // Map Arabic country names to English for URL
        const countryMap = {
          السودان: "sudan",
          مصر: "egypt",
          السعودية: "saudi",
          الإمارات: "uae",
          "باقي دول العالم": "restOfWorld",
        };

        // Map period names to URL format
        const periodMap = {
          day: "daily",
          weekly: "weekly",
          month: "monthly",
          year: "yearly",
        };

        const countryParam = countryMap[this.selectedCountry];
        const periodParam =
          periodMap[this.selectedPlan.period] || this.selectedPlan.period;

        if (countryParam && periodParam) {
          this.$router.push({
            name: "auth.subscriptions.plans.country.period",
            params: { country: countryParam, period: periodParam },
          });
        } else {
          this.toast.error("خطأ في معاملات التوجيه");
        }
        return;
      }

      // Handle other countries
      if (!this.selectedPaymentMethod) {
        this.toast.warning("يرجى اختيار وسيلة الدفع");
        return;
      }

      console.log(
        `Redirecting to: ${this.selectedPaymentMethod} for ${this.selectedCountry}`
      );

      if (
        this.selectedPaymentMethod === "Stripe" ||
        this.selectedPaymentMethod === "سترايب"
      ) {
        this.$router.push({ name: "stripe-payment" });
      } else {
        this.toast.info(`جاري التحويل إلى ${this.selectedPaymentMethod}`);
        // Add specific routing logic for each payment method
      }
    },
  },
  async created() {
    const userID = this.$route.query.user_id;
    const origin = this.$route.query.origin || "web";
    localStorage.setItem("origin", origin);
    //Get the conutry and it could be AE,SD,EG,SA,
    const countryCode = this.$route.query.country;
    // make sure it is all upper case
    localStorage.setItem("user_id_sub", userID);

    const upperCaseCountryCode = countryCode ? countryCode.toUpperCase() : null;
    this.selectedCountry = this.conutries[upperCaseCountryCode] || "السودان";
    const temp_access_token = this.$route.query.ott;
    if (temp_access_token) {
      await this.getTokenAccess(temp_access_token, userID).then((response) => {
        console.log("Access token set from OTT", response?.data?.success);
      });
    }
  },
};
</script>

<style scoped>
/* Ensure proper height constraints for viewport */
.min-h-screen {
  height: 100vh;
  max-height: 100vh;
}

/* Custom scrollbar for the main content area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #9d2890;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #b83bb1;
}

/* Custom dropdown arrow styling */
.custom-select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: left 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-left: 2.5rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
